import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { ENTRY_POINTS, type EntryId, entryForAd } from './blueprint';
import { MetaShell } from './MetaShell';
import { costPer, type Flat, flatAds, int, isLive, kr, resultKind, useMetaStructure } from './model';

/*
 * Meta → Inngangsdører. The five doors a customer comes in through, each with
 * who she is, what she fears, what we promise, and what the account has
 * already spent on that door. Ads are sorted into doors by their names
 * (entryForAd), so the numbers are honest only as far as the names are.
 */

const PROOF = {
  bevist: { label: 'Bevist i tallene', tone: 'good' as const },
  lovende: { label: 'Lovende', tone: 'brand' as const },
  utestet: { label: 'Nesten ikke testet', tone: 'warn' as const },
};

export function MetaDoorsPage() {
  const q = useMetaStructure();
  const d = q.data?.data;
  if (q.isLoading) return <LoadingState />;
  if (q.isError || !d) return <ErrorState onRetry={() => void q.refetch()} />;

  const fx = d.fxAedToNok;
  const byDoor = new Map<EntryId | null, Flat[]>();
  for (const f of flatAds(d)) {
    const k = entryForAd(f.ad.name);
    byDoor.set(k, [...(byDoor.get(k) ?? []), f]);
  }
  const unsorted = byDoor.get(null) ?? [];

  return (
    <MetaShell
      title="Inngangsdører"
      lead={
        <>
          En inngangsdør er grunnen til at noen klikker: smerten, frykten eller drømmen annonsen
          snakker til. Hver annonse åpner én dør. Tallene under er alt kontoen har brukt på hver dør
          siden mai, sortert etter annonsenavn.
        </>
      }
    >
      <div className="grid gap-3 lg:grid-cols-2">
        {ENTRY_POINTS.map((e) => {
          const ads = byDoor.get(e.id) ?? [];
          const spend = ads.reduce((n, f) => n + f.ad.lifetime.spend, 0) * fx;
          const bookings = ads.reduce((n, f) => n + f.ad.lifetime.schedules, 0);
          const leads = ads.reduce((n, f) => n + f.ad.lifetime.leads, 0);
          const live = ads.filter((f) => isLive(f.ad) && isLive(f.adset) && isLive(f.campaign)).length;
          const best = [...ads]
            .filter((f) => f.ad.lifetime.spend * fx >= 150)
            .map((f) => ({ f, c: costPer(f.ad.lifetime, resultKind(f.adset), fx) }))
            .filter((x) => x.c !== null)
            .sort((a, b) => (a.c ?? 0) - (b.c ?? 0))[0];
          return (
            <Card key={e.id} className="flex flex-col gap-3 p-3 sm:p-4" style={{ borderTop: `3px solid ${e.color}` }}>
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-section text-fg">{e.name}</h2>
                <Badge tone={PROOF[e.proof].tone}>{PROOF[e.proof].label}</Badge>
              </div>
              <dl className="grid gap-1.5 text-body">
                <Row k="Hvem" v={e.who} />
                <Row k="Frykter" v={e.fear} />
                <Row k="Vi lover" v={e.promise} />
                <Row k="Lander på" v={<code className="text-label">{e.landing}</code>} />
              </dl>

              <div className="grid grid-cols-4 gap-2 rounded-md bg-surface-2 p-2.5 text-label">
                <Stat k="Annonser" v={`${ads.length}${live ? ` (${live} aktive)` : ''}`} />
                <Stat k="Brukt" v={kr(spend)} />
                <Stat k="Bookinger" v={int(bookings)} />
                <Stat k="Skjemalead" v={int(leads)} />
              </div>
              {best && (
                <p className="text-label text-fg-muted">
                  Beste annonse: <span className="text-fg">«{best.f.ad.name}»</span>, {kr(best.c)} per{' '}
                  {resultKind(best.f.adset) === 'booking' ? 'booking' : 'lead'}.
                </p>
              )}

              <div>
                <div className="mb-1 text-micro font-semibold uppercase tracking-wider text-fg-subtle">Bevis</div>
                <ul className="flex list-disc flex-col gap-0.5 pl-4 text-label text-fg-muted">
                  {e.evidence.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="mb-1 text-micro font-semibold uppercase tracking-wider text-fg-subtle">
                  Hooks å lage (forslag)
                </div>
                <ul className="flex flex-col gap-1">
                  {e.hooks.map((h) => (
                    <li key={h} className="rounded-md border border-dashed border-border px-2.5 py-1.5 text-label text-fg">
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          );
        })}
      </div>

      {unsorted.length > 0 && (
        <Card className="p-3 sm:p-4">
          <h2 className="text-section text-fg">Annonser uten dør ({unsorted.length})</h2>
          <p className="mt-0.5 text-label text-fg-muted">
            Navnet sier ikke hvilken dør de åpner. I systemet får hver annonse en dør i navnet, så dette
            blir tomt. Brukt til sammen: {kr(unsorted.reduce((n, f) => n + f.ad.lifetime.spend, 0) * fx)}.
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {unsorted
              .sort((a, b) => b.ad.lifetime.spend - a.ad.lifetime.spend)
              .map((f) => (
                <span key={f.ad.id} className="rounded-full bg-surface-3 px-2 py-0.5 text-micro text-fg-muted">
                  {f.ad.name}
                </span>
              ))}
          </div>
        </Card>
      )}
    </MetaShell>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[72px_1fr] gap-2">
      <dt className="text-label text-fg-subtle">{k}</dt>
      <dd className="text-fg">{v}</dd>
    </div>
  );
}
function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className="min-w-0">
      <div className="text-micro text-fg-subtle">{k}</div>
      <div className="tabular truncate font-medium text-fg">{v}</div>
    </div>
  );
}
